import { useMemo } from 'react';
import {
  useLazyGetIntegrationTrustpilotLocationsQuery,
  useLazyGetIntegrationTrustpilotTemplatesQuery,
  useSaveIntegrationTrustpilotConfigMutation,
} from '@/redux/api/api.caller';
import { IIntegrationTrustpilot, IIntegrationTrustpilotConfig } from '@/types/settings';
import { IntegrationConfigTab, IntegrationStatus } from '../../types';
import useConfigDraft from '../useConfigDraft';

const DEFAULT_CONFIG = {
  reviewTarget: 'store',
  storeTemplateId: '',
  productTemplateId: '',
  senderName: '',
  senderEmail: '',
  replyTo: '',
  senderLocationId: '',
  storePreferredSendTime: '0',
  productPreferredSendTime: '0',
  customerEmailLocale: 'customer',
};

const SEND_TIME_OPTIONS = [
  { label: 'Immediately', value: '0' },
  { label: 'After 1 hour', value: '1h' },
  { label: 'After 1 day', value: '1d' },
  { label: 'After 3 days', value: '3d' },
  { label: 'After 7 days', value: '7d' },
  { label: 'After 14 days', value: '14d' },
];

const DEFAULT_TEMPLATE_OPTION = { label: 'Trustpilot default template', value: '' };
const DEFAULT_LOCATION_OPTION = { label: 'No specific location', value: '' };

const useTrustpilotConfigTab = (status: IIntegrationTrustpilot | undefined, integrationStatus: IntegrationStatus) => {
  const [fetchTemplates, { data, isFetching, isError: isTemplatesError }] = useLazyGetIntegrationTrustpilotTemplatesQuery();
  const [fetchLocations, { data: locationsData, isFetching: isFetchingLocations }] = useLazyGetIntegrationTrustpilotLocationsQuery();
  const [saveConfig, { isLoading: isSaving }] = useSaveIntegrationTrustpilotConfigMutation();

  const buildOptions = (type: string) => {
    const options = (data?.data || [])
      .filter((t) => t.type === type)
      .sort((a, b) => Number(b.isDefault) - Number(a.isDefault))
      .map((t) => ({ label: t.name, value: t.id, hint: t.isDefault ? 'Default template' : undefined }));
    return options.length ? options : [DEFAULT_TEMPLATE_OPTION];
  };
  const storeOptions = useMemo(() => buildOptions('ServiceReview'), [data]); // eslint-disable-line react-hooks/exhaustive-deps
  const productOptions = useMemo(() => buildOptions('ProductReview'), [data]); // eslint-disable-line react-hooks/exhaustive-deps
  const locationOptions = useMemo(() => {
    const options = (locationsData?.data || []).map((l) => ({ label: l.name, value: l.id }));
    return options.length ? [DEFAULT_LOCATION_OPTION, ...options] : [DEFAULT_LOCATION_OPTION];
  }, [locationsData]);

  const saved = useMemo(() => ({ ...DEFAULT_CONFIG, ...status?.config }), [status?.config]);

  const draft = useConfigDraft({
    saved,
    isSaving,
    resolve: (values) => ({
      ...values,
      storeTemplateId: values.storeTemplateId || storeOptions[0]?.value || '',
      productTemplateId: values.productTemplateId || productOptions[0]?.value || '',
    }),
    save: (values) => {
      const payload = { ...values };
      if (isTemplatesError) {
        delete payload.storeTemplateId;
        delete payload.productTemplateId;
      }
      return saveConfig(payload as unknown as IIntegrationTrustpilotConfig);
    },
  });

  if (integrationStatus === 'disconnected') return undefined;

  const configTab: IntegrationConfigTab = {
    ...draft,
    isLoading: isFetching || isFetchingLocations,
    options: {
      storeTemplateId: storeOptions,
      productTemplateId: productOptions,
      preferredSendTime: SEND_TIME_OPTIONS,
      senderLocationId: locationOptions,
    },
    onActivate: () => {
      if (integrationStatus === 'active') {
        fetchTemplates(undefined, true);
        fetchLocations(undefined, true);
      }
    },
  };
  return configTab;
};

export default useTrustpilotConfigTab;
